import React, { useState, useRef } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { getFullUrl, parseCssString } from '../../utils/utils';
import RippleButton from './RippleButton';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import PropTypes from 'prop-types';
import LoginAvatar from '../../images/login_avatar.svg';
import LoginModal from './LoginModal';

const Navbar = ({
  NavLogo,
  logoClasses = 'w-60 sm:w-64 md:w-80 lg:w-auto max-w-80',
  subdomain,
  menuItems,
  typeOf,
}) => {
  const menuRef = useRef();
  const closeButtonRef = useRef();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const isLoggedIn = !!localStorage.getItem('auth_token');

  useOnClickOutside(menuRef, () => setIsMenuOpen(false), closeButtonRef);

  return (
    <nav className="relative z-10 border-b-[1px] border-gray-50 border-opacity-30 py-1">
      <div className="mx-auto flex justify-between items-center px-4 sm:px-8 py-1 pt-2
       lg:py-3 page-navbar">
        <button ref={closeButtonRef} onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden w-10">
          {
            isMenuOpen ? <XMarkIcon  className="h-8 w-8 sm:h-10 sm:w-10" /> :
            <Bars3Icon className="h-8 w-8 sm:h-10 sm:w-10" />
          }
        </button>

        <a href={getFullUrl('/', subdomain)} className="lg:mr-auto">
          <img src={NavLogo} alt="Nav Logo" className={`min-h-10 ${logoClasses}`} />
        </a>

        {isLoggedIn ? (
          <a href={getFullUrl('/users/edit', subdomain)} className="w-10 lg:hidden">
            <img src={LoginAvatar} alt="Account" className="h-8 w-8 sm:h-10 sm:w-10" />
          </a>
        ) : (
          <button onClick={() => setIsLoginModalOpen(true)} className="w-10 lg:hidden">
            <img src={LoginAvatar} alt="Login" className="h-8 w-8 sm:h-10 sm:w-10" />
          </button>
        )}

        {/* Menu Items for mobile */}
        {menuItems.length > 0 && (
          <Transition
            ref={menuRef}
            show={isMenuOpen}
            enter="transition ease-out duration-300 transform"
            enterFrom="opacity-0 -translate-y-full"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-200 transform"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-full"
            className="fixed inset-x-0 top-[60px] sm:top-[60px] lg:hidden z-50 shadow-md bg-white"
          >
            <ul className="flex flex-col">
              {menuItems.slice(0, -1).map((item) => (
                <RippleButton
                  key={item.name}
                  onClick={() => window.location.href = getFullUrl(item.path, subdomain)}
                  className={
                    `flex items-center justify-start h-16 pl-8 text-base border-b-[1px] border-gray-200
                    ${item.extraClasses || ''}`
                  }
                  style={parseCssString(item.inlineStyles)}
                >
                  <span>{item.name}</span>
                </RippleButton>
              ))}
              <RippleButton
                onClick={() => window.location.href = getFullUrl(menuItems[menuItems.length - 1].path, subdomain)}
                className={`
                  flex items-center justify-center ${menuItems[menuItems.length - 1].customClasses}
                  rounded-full py-2 px-4 my-6 h-[48px] w-3/4 max-w-72 mx-auto
                `}
                style={parseCssString(menuItems[menuItems.length - 1].inlineStyles)}
              >
                <span>{menuItems[menuItems.length - 1].name}</span>
              </RippleButton>
            </ul>
          </Transition>
        )}

        {/* Menu Items for desktop */}
        <ul className="hidden lg:flex items-center space-x-4">
          {menuItems.map((item) => (
            <li
              key={item.name}
              onClick={() => window.location.href = getFullUrl(item.path, subdomain)}
              className={
                `${typeOf !== 'tac_landing' && typeOf !== 'registration' 
                  ? 'text-white' : ''} text-base cursor-pointer ${item.customClasses}
                ${item.extraClasses || ''}`}
              style={parseCssString(item.inlineStyles)}
            >
              <span>{item.name}</span>
            </li>
          ))}
        </ul>
      </div>
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
    </nav>
  );
};

Navbar.propTypes = {
  NavLogo: PropTypes.string.isRequired,
  logoClasses: PropTypes.string,
  subdomain: PropTypes.string,
  typeOf: PropTypes.string,
  showExtraLinks: PropTypes.bool,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      order: PropTypes.number,
      path: PropTypes.string.isRequired,
      customClasses: PropTypes.string,
      inlineStyles: PropTypes.string,
    }).isRequired
  ).isRequired,
};

Navbar.defaultProps = {
  defaultMenuItems: null
}

export default Navbar;
